import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { TableCellEditShell } from './TableCellEditShell';
import { Sheet, SheetLayer } from '../types';
import { getPrimaryLayer, resolveActiveLayerId } from '../logic/LayerLogic';
import { GuideContext } from '../contexts/GuideContext';
import { useLocale } from '../contexts/LocaleContext';
import { handleLayerRemoveButtonClick, clearLayerRemoveConfirming } from '../utils/layerRemoveButton';
import {
  formatLayerAddButtonGuideHtml,
  formatLayerListExtendedGuideHtml,
  formatLayerNameEditingGuideHtml,
  formatLayerRemoveButtonGuideHtml,
  formatLayerTableHeaderCellGuideHtml,
} from '../utils/tooltipFormatter';
import './styles/SheetLayerTableHeader.scss';

const LAYER_HEADER_GUIDE_HOVER_MS = 2000;

interface TableColumnLayout {
  layerStart: number;
  mult: number;
  count: number;
  total: number;
  colCount: number;
}

interface SheetLayerTableHeaderProps {
  sheet: Sheet;
  layers: SheetLayer[];
  cols: TableColumnLayout;
  rowIndex: number;
  isOpen: boolean;
  editingCell: { row: number; cell: number } | null;
  editValue: string;
  setEditValue: (v: string) => void;
  editInputRef: React.RefObject<HTMLInputElement | null>;
  isComposing: boolean;
  setIsComposing: (v: boolean) => void;
  getCellSelectionClasses: (rowIndex: number, cellIndex: number) => string;
  handleCellFocus: (rowIndex: number, cellIndex: number) => void;
  handleCellKeyDown: (e: React.KeyboardEvent<HTMLTableCellElement>) => void;
  handleCellKeyDownForEdit: (
    e: React.KeyboardEvent,
    rowIndex: number,
    cellIndex: number,
    currentValue: string
  ) => void;
  handleEditComplete: () => void;
  handleEditCancel: () => void;
  handleDoubleClick: (
    e: React.MouseEvent,
    rowIndex: number,
    cellIndex: number,
    currentValue: string
  ) => void;
  handleDoubleTouch: (
    e: React.TouchEvent,
    rowIndex: number,
    cellIndex: number,
    currentValue: string
  ) => void;
  onSelectLayer: (layerId: string) => void;
  onAddLayer: () => void;
  onRemoveLayer?: (layerId: string) => void;
  scrollContainerRef?: React.RefObject<HTMLDivElement | null>;
  setSelectionStart: (s: { row: number; cell: number } | null) => void;
  setSelectionEnd: (s: { row: number; cell: number } | null) => void;
  consumeIgnoredClick?: () => boolean;
}

export const SheetLayerTableHeader: React.FC<SheetLayerTableHeaderProps> = ({
  sheet,
  layers,
  cols,
  rowIndex,
  isOpen,
  editingCell,
  editValue,
  setEditValue,
  editInputRef,
  isComposing,
  setIsComposing,
  getCellSelectionClasses,
  handleCellFocus,
  handleCellKeyDown,
  handleCellKeyDownForEdit,
  handleEditComplete,
  handleEditCancel,
  handleDoubleClick,
  handleDoubleTouch,
  onSelectLayer,
  onAddLayer,
  onRemoveLayer,
  scrollContainerRef,
  setSelectionStart,
  setSelectionEnd,
  consumeIgnoredClick,
}) => {
  const isGuideEnabled = useContext(GuideContext);
  const { appCopy } = useLocale();
  const activeId = resolveActiveLayerId(sheet);
  const activeLayer = layers.find(l => l.id === activeId) ?? layers[0];
  const layerLabelName = activeLayer?.name?.trim() || appCopy.layer.defaultName;
  const headerRefs = useRef<Record<string, HTMLTableCellElement | null>>({});
  const headerGuideTimerRef = useRef<number | null>(null);
  const [headerGuideTier, setHeaderGuideTier] = useState(0);

  useEffect(() => {
    clearLayerRemoveConfirming();
  }, [activeId]);

  useEffect(() => {
    return () => {
      if (headerGuideTimerRef.current != null) {
        window.clearTimeout(headerGuideTimerRef.current);
      }
    };
  }, []);

  const clearHeaderGuideTimer = () => {
    if (headerGuideTimerRef.current != null) {
      window.clearTimeout(headerGuideTimerRef.current);
      headerGuideTimerRef.current = null;
    }
  };

  const headerRowTooltipHtml = useMemo(() => {
    if (!isGuideEnabled) return undefined;
    const base = formatLayerTableHeaderCellGuideHtml(true);
    if (headerGuideTier < 2) return base || undefined;
    const ext = formatLayerListExtendedGuideHtml(layers.length > 1);
    return ext ? `${base}<br/>${ext}` : base || undefined;
  }, [isGuideEnabled, headerGuideTier, layers.length]);

  const renderCompositeLabel = (suffix: string) => (
    <span className="layer-header-composite-label">
      <span className="layer-header-composite-name">{layerLabelName}</span>
      <span className="layer-header-composite-suffix">{suffix}</span>
    </span>
  );

  useEffect(() => {
    const container = scrollContainerRef?.current;
    if (!container) return;

    const primaryLayerId = getPrimaryLayer(sheet).id;
    if (activeId === primaryLayerId) {
      container.scrollTo({ left: 0, top: container.scrollTop, behavior: 'auto' });
      return;
    }

    const el = headerRefs.current[activeId];
    if (el) {
      el.scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: 'smooth' });
    }
  }, [activeId, scrollContainerRef, sheet]);

  const activateLayerColumn = (
    e: React.SyntheticEvent<HTMLTableCellElement>,
    layer: SheetLayer,
    cellIndex: number
  ) => {
    if (consumeIgnoredClick?.()) return;
    if (editingCell) {
      handleEditComplete();
      return;
    }
    e.currentTarget.focus();
    setSelectionStart({ row: rowIndex, cell: cellIndex });
    setSelectionEnd({ row: rowIndex, cell: cellIndex });
    void Promise.resolve(onSelectLayer(layer.id));
  };

  const canRemoveLayer = layers.length > 1 && !!onRemoveLayer;

  const renderLayerCell = (layer: SheetLayer, layerIndex: number) => {
    const cellIndex = cols.layerStart + layerIndex;
    const displayName = layer.name || appCopy.layer.defaultName;
    const isEditing =
      editingCell?.row === rowIndex && editingCell?.cell === cellIndex;

    return (
      <th
        key={layer.id}
        ref={el => { headerRefs.current[layer.id] = el; }}
        className={`layer-col-value table-col-layer ${getCellSelectionClasses(rowIndex, cellIndex)}${layer.id === activeId ? ' layer-column-active' : ''}${canRemoveLayer && !isEditing && layer.id === activeId ? ' layer-removable' : ''}${layerIndex === layers.length - 1 ? ' layer-col-value-last' : ''}`}
        tabIndex={isOpen ? 4000 : -1}
        data-tooltip-id="tt"
        data-tooltip-html={
          isEditing
            ? formatLayerNameEditingGuideHtml(layers.length > 1) || undefined
            : formatLayerTableHeaderCellGuideHtml(layer.id === activeId) || undefined
        }
        data-row-index={rowIndex}
        data-cell-index={cellIndex}
        onKeyDown={e => {
          if (isEditing) return;
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleDoubleClick(e as unknown as React.MouseEvent, rowIndex, cellIndex, displayName);
            return;
          }
          if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown', 'Tab'].includes(e.key)) {
            e.preventDefault();
            handleCellKeyDown(e as React.KeyboardEvent<HTMLTableCellElement>);
            return;
          }
          handleCellKeyDownForEdit(e, rowIndex, cellIndex, displayName);
        }}
        onFocus={() => handleCellFocus(rowIndex, cellIndex)}
        onClick={e => {
          if ((e.target as Element).closest('.layer-add-btn, .layer-remove-btn')) return;
          activateLayerColumn(e, layer, cellIndex);
        }}
        onMouseDown={e => {
          if ((e.target as Element).closest('.layer-add-btn, .layer-remove-btn')) return;
          e.currentTarget.focus();
        }}
        onDoubleClick={e => handleDoubleClick(e, rowIndex, cellIndex, displayName)}
        onTouchEnd={e => {
          if (
            e.target instanceof Element &&
            (e.target.closest('.layer-add-btn') || e.target.closest('.layer-remove-btn'))
          ) {
            return;
          }
          if (e.changedTouches.length !== 1) return;
          const host = e.currentTarget as HTMLElement & { _lastTouchTime?: number };
          const now = Date.now();
          const last = host._lastTouchTime || 0;
          if (now - last < 300) {
            handleDoubleTouch(e, rowIndex, cellIndex, displayName);
            host._lastTouchTime = now;
            return;
          }
          host._lastTouchTime = now;
          activateLayerColumn(e, layer, cellIndex);
        }}
      >
        <TableCellEditShell
          isEditing={isEditing}
          align="center"
          measure={<span className="layer-header-name">{displayName}</span>}
        >
          {isEditing ? (
            <input
              ref={editInputRef}
              type="text"
              value={editValue}
              data-table-editing="true"
              onChange={e => setEditValue(e.target.value)}
              onMouseDown={e => e.stopPropagation()}
              onClick={e => e.stopPropagation()}
              onTouchStart={e => e.stopPropagation()}
              onTouchEnd={e => e.stopPropagation()}
              onBlur={handleEditComplete}
              onKeyDown={e => {
                if (isComposing) return;
                if (e.key === 'Backspace' || e.key === 'Delete') {
                  e.stopPropagation();
                  return;
                }
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  e.stopPropagation();
                  handleEditComplete();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  e.stopPropagation();
                  handleEditCancel();
                } else if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                  e.stopPropagation();
                }
              }}
              onCompositionStart={() => setIsComposing(true)}
              onCompositionEnd={() => setIsComposing(false)}
              autoFocus
              className="table-cell-edit-input"
            />
          ) : null}
        </TableCellEditShell>
        {canRemoveLayer && !isEditing ? (
          <button
            type="button"
            className="layer-remove-btn"
            data-layer-id={layer.id}
            data-tooltip-id="tt"
            data-tooltip-html={
              layer.id === activeId
                ? formatLayerRemoveButtonGuideHtml() || undefined
                : undefined
            }
            aria-label={appCopy.layer.removeAria(displayName)}
            aria-hidden={layer.id !== activeId}
            tabIndex={layer.id === activeId ? undefined : -1}
            onMouseDown={e => e.stopPropagation()}
            onTouchStart={e => e.stopPropagation()}
            onClick={e => {
              if (layer.id !== activeId) return;
              handleLayerRemoveButtonClick(e, layer.id, id => onRemoveLayer!(id));
            }}
            onTouchEnd={e => {
              if (layer.id !== activeId) return;
              handleLayerRemoveButtonClick(e, layer.id, id => onRemoveLayer!(id));
            }}
          >
            −
          </button>
        ) : null}
        {layerIndex === layers.length - 1 ? (
          <button
            type="button"
            className="layer-add-btn"
            data-tooltip-id="tt"
            data-tooltip-html={formatLayerAddButtonGuideHtml() || undefined}
            onMouseDown={e => e.stopPropagation()}
            onTouchStart={e => e.stopPropagation()}
            onClick={e => {
              e.stopPropagation();
              onAddLayer();
            }}
            onTouchEnd={e => {
              e.stopPropagation();
              e.preventDefault();
              onAddLayer();
            }}
            title={appCopy.layer.addAria}
            aria-label={appCopy.layer.addAria}
          >
            +
          </button>
        ) : null}
      </th>
    );
  };

  const selectableHeaderCell = (
    cellIndex: number,
    className: string,
    colClass: string,
    content?: React.ReactNode
  ) => (
    <th
      key={`${className}-${cellIndex}`}
      className={`${className} ${colClass} ${getCellSelectionClasses(rowIndex, cellIndex)}`.trim()}
      tabIndex={isOpen ? 4000 : -1}
      data-row-index={rowIndex}
      data-cell-index={cellIndex}
      onKeyDown={handleCellKeyDown}
      onFocus={() => handleCellFocus(rowIndex, cellIndex)}
      onClick={e => {
        if (consumeIgnoredClick?.()) return;
        if (editingCell) {
          handleEditComplete();
          return;
        }
        e.currentTarget.focus();
        setSelectionStart({ row: rowIndex, cell: cellIndex });
        setSelectionEnd({ row: rowIndex, cell: cellIndex });
      }}
      onMouseDown={e => e.currentTarget.focus()}
    >
      {content}
    </th>
  );

  return (
    <thead
      className="sheet-layer-header"
      data-tooltip-id="tt"
      data-tooltip-html={headerRowTooltipHtml}
      data-tooltip-float={true}
      onMouseEnter={() => {
        if (!isGuideEnabled) return;
        clearHeaderGuideTimer();
        headerGuideTimerRef.current = window.setTimeout(() => {
          setHeaderGuideTier(2);
        }, LAYER_HEADER_GUIDE_HOVER_MS);
      }}
      onMouseLeave={() => {
        clearHeaderGuideTimer();
        setHeaderGuideTier(0);
      }}
    >
      <tr data-row-index={rowIndex}>
        {selectableHeaderCell(0, 'layer-col-sticky layer-col-title', 'table-col-title', appCopy.table.header.title)}
        {layers.map(renderLayerCell)}
        {selectableHeaderCell(cols.mult, 'layer-col-mult', 'table-col-mult-op')}
        {selectableHeaderCell(cols.count, 'layer-col-count', 'table-col-count', appCopy.table.header.qty)}
        {selectableHeaderCell(
          cols.total,
          'layer-col-total',
          'table-col-total',
          renderCompositeLabel(appCopy.table.header.amountSuffix)
        )}
      </tr>
    </thead>
  );
};
